import {
  AllowedUserActionItem,
  CartItem,
  CartPromotion,
  CartPromotionGroup,
} from '@teko/cart/v2';
import { getProductUrl } from '@teko/discovery';
import { t } from 'i18n';
import { IConditionProduct } from 'types';

export * from './cartItem';

/**
 * Returns all promotions (not being grouped)
 *
 * @returns all promotions
 * @param promotionGroups: list of promotion groups (from API response)
 */
export const flattenPromotionsFromPromotionGroups = (
  promotionGroups: CartPromotionGroup[],
): CartPromotion[] => {
  return (
    promotionGroups.reduce<CartPromotion[]>(
      (pre, { promotions: promotionGroup }) => [...pre, ...promotionGroup],
      [],
    ) || []
  );
};

/**
 * Extract all sku and quantity of childItems
 *
 * @returns array of childItems sku and quantity
 * @param childItems - array of item
 * @param productQuantity - combo's quantity
 */
export const getComboLineItems = (
  childItems: CartItem[],
  productQuantity: number,
) => {
  // get 1 level only
  return childItems.map((product) => ({
    sku: product.sku,
    quantity: product.quantity / productQuantity,
  }));
};

/**
 * Return the main promotionId of a combo products
 *
 * @returns the id of the first promotion
 * @param promotionGroups: list of promotion groups (from API response)
 */
export const getComboPromotionId = (
  promotionGroups: CartPromotionGroup[],
): number => {
  // all promotions of an item (not being grouped)
  const allItemPromotions =
    flattenPromotionsFromPromotionGroups(promotionGroups);

  return !!allItemPromotions.length ? allItemPromotions[0].promotionId : 0;
};

/**
 * Returns the link object with the item information
 * Note: only return link when this item's allowedUserAction contains OPEN_PRODUCT_DETAIL value
 *
 * @param item (CartItem)
 * @returns The link Object
 *
 */
export const getItemLinkObj = (item: CartItem) => {
  if (
    !item.allowedUserActions ||
    !item.allowedUserActions.includes(AllowedUserActionItem.OPEN_PRODUCT_DETAIL)
  )
    return null;
  return {
    as: getProductUrl(item.name, item.sku),
    href: { pathname: '/products/[sku]', query: { sku: item.sku } },
    passHref: true,
  };
};

/**
 * Returns the link object with the condition product information
 *
 * @param item (IConditionProduct)
 * @returns The link Object
 *
 */
export const getConditionProductLinkObj = (item: IConditionProduct) => {
  return {
    as: getProductUrl(item.name, item.sku),
    href: { pathname: '/products/[sku]', query: { sku: item.sku } },
    passHref: true,
  };
};

/**
 * return cart title base on orderId and isClone properties
 * @param orderId
 * @param isClone
 * @returns
 */
export const getCartTitle = (
  orderId: string,
  isClone: boolean,
  numberOfItems: number,
): string => {
  // case checkout new order
  if (!orderId) {
    return `${t('Giỏ hàng')} (${numberOfItems})`;
  }

  // case clone order
  if (isClone) {
    return t(`Giỏ hàng (copy đơn {{orderId}})`, { orderId });
  }

  // case edit order
  return t(`Sửa đơn hàng {{orderId}}`, { orderId });
};

export const getComboImageUrl = (childItems: CartItem[]): string[] => {
  if (childItems.length === 0) return [];
  return childItems.map((item) => item.imageUrl || '');
};
